import { DataField } from "@iventis/domain-model/model/dataField";
import type { MapObjectProperties } from "@iventis/map-types";
import type {
    Map,
    FillLayerSpecification,
    FillExtrusionLayerSpecification,
    LineLayerSpecification,
    ModelLayerSpecification,
    PaintSpecification,
    LayoutSpecification,
} from "@iventis/mapbox-gl";
import { capitalise } from "@iventis/utilities/src/stringhandling/capitalise";
import type { Feature, Geometry } from "geojson";
import { LayerSourceType } from "../../types/internal";
import { IMapboxLayer } from "./mapbox-layer";
import { MapboxLocalLayer } from "./mapbox-local-layer";
import { MapboxRemoteLayer } from "./mapbox-remote-layer";
import { updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";

type SupportedMapboxLayers = FillLayerSpecification | FillExtrusionLayerSpecification | LineLayerSpecification | ModelLayerSpecification;

export interface IMapboxLayerManager<TSource extends Feature<Geometry, MapObjectProperties>[]> extends Omit<IMapboxLayer, "addLayer"> {
    hasLocalLayer: boolean;
    hasRemoteLayer: boolean;
    localLayerId: string;
    addLayerToMapbox: (layer: SupportedMapboxLayers, options: { layerSourceType: LayerSourceType; aboveLayerId: string }) => void;
    createLocalLayerClass: () => void;
    createRemoteLayerClass: () => void;
    addLocalSource(): void;
    updateLocalSource(source: TSource): void;
    removeLocalSource(): void;
    getActiveLayerIds: () => string[];
    setPaintProperty<T extends keyof PaintSpecification>(name: T, value: PaintSpecification[T]): void;
    setLayoutProperty<T extends keyof LayoutSpecification>(name: T, value: LayoutSpecification[T]): void;
    hideRemoteObjects(objectIds: string[]): void;
}

/** Manages both local and remote layers for one iventis layer */
export class MapboxLayerManager<TSource extends Feature<Geometry, MapObjectProperties>[]> implements IMapboxLayerManager<TSource> {
    private mapboxLocalLayer?: MapboxLocalLayer<TSource, SupportedMapboxLayers>;

    private mapboxRemoteLayer?: MapboxRemoteLayer;

    private get mapboxLayers() {
        return { local: this.mapboxLocalLayer, remote: this.mapboxRemoteLayer };
    }

    public get hasLocalLayer() {
        return this.mapboxLocalLayer != null;
    }

    public get hasRemoteLayer() {
        return this.mapboxRemoteLayer != null;
    }

    public get localLayerId() {
        return this.mapboxLocalLayer?.layerId;
    }

    constructor(private map: Map, private layerId: string) {}

    public createLocalLayerClass() {
        // We are assuming here that if there isn't a remote layer by now, there will never be a remote layer, since remote layers are the first to be added.
        const localLayerId = this.hasRemoteLayer ? `${this.layerId}_local` : this.layerId;
        this.mapboxLocalLayer = new MapboxLocalLayer(this.map, localLayerId, localLayerId);
    }

    public createRemoteLayerClass() {
        if (this.mapboxLocalLayer != null) {
            throw new Error("If a remote layer is being created, it needs to be created before the local layer");
        }

        this.mapboxRemoteLayer = new MapboxRemoteLayer(this.map, this.layerId);
    }

    public addLayerToMapbox(layer: SupportedMapboxLayers, options: { layerSourceType: LayerSourceType; aboveLayerId: string }): void {
        const mapboxLayer = this.mapboxLayers[options.layerSourceType];
        if (mapboxLayer == null) {
            throw new Error(`Make sure to run create${capitalise(options.layerSourceType)}Layer before adding it.`);
        }
        mapboxLayer.addLayer(layer, options.aboveLayerId);
    }

    public removeLayer() {
        this.mapboxLocalLayer?.removeLayer();
        this.mapboxRemoteLayer?.removeLayer();
    }

    public addLocalSource() {
        if (!this.hasLocalLayer) {
            this.createLocalLayerClass();
        }
        this.mapboxLocalLayer?.addSource();
    }

    public updateLocalSource(source: TSource): void {
        if (!this.hasLocalLayer) {
            this.createLocalLayerClass();
        }
        this.mapboxLocalLayer?.updateSource(source);
    }

    public removeLocalSource(): void {
        this.mapboxLocalLayer?.removeSource();
    }

    public hideLayer(): void {
        this.mapboxLocalLayer?.hideLayer();
        this.mapboxRemoteLayer?.hideLayer();
    }

    public showLayer(): void {
        this.mapboxLocalLayer?.showLayer();
        this.mapboxRemoteLayer?.showLayer();
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.mapboxRemoteLayer?.updateMapOrder(aboveLayerId);
        // If there is no remote layer then we just need to order the local layer
        this.mapboxLocalLayer?.updateMapOrder(this.hasRemoteLayer ? this.mapboxRemoteLayer.layerId : aboveLayerId);
    }

    public updateMapLevel(level: number): void {
        this.mapboxLocalLayer?.updateMapLevel(level);
        this.mapboxRemoteLayer?.updateMapLevel(level);
    }

    public addDateFilter(dataFields: DataField[], day: number, time: number) {
        this.mapboxLocalLayer?.addDateFilter();
        this.mapboxRemoteLayer?.addDateFilter(dataFields, day, time);
    }

    public removeDateFilter() {
        this.mapboxLocalLayer?.removeDateFilter();
        this.mapboxRemoteLayer?.removeDateFilter();
    }

    public getActiveLayerIds() {
        const layerIds: string[] = [];
        if (this.hasLocalLayer) {
            layerIds.push(this.mapboxLocalLayer.layerId);
        }
        if (this.hasRemoteLayer) {
            layerIds.push(this.mapboxRemoteLayer.layerId);
        }
        return layerIds;
    }

    public setPaintProperty<T extends keyof PaintSpecification>(name: T, value: PaintSpecification[T]): void {
        this.map.setPaintProperty(this.layerId, name, value);
        if (this.hasRemoteLayer && this.hasLocalLayer) {
            this.map.setPaintProperty(`${this.layerId}_local`, name, value);
        }
    }

    public setLayoutProperty<T extends keyof LayoutSpecification>(name: T, value: LayoutSpecification[T]): void {
        this.map.setLayoutProperty(this.layerId, name, value);
        if (this.hasRemoteLayer && this.hasLocalLayer) {
            this.map.setLayoutProperty(`${this.layerId}_local`, name, value);
        }
    }

    public hideRemoteObjects(objectIds: string[]) {
        if (this.mapboxRemoteLayer == null) {
            return;
        }
        const currentFilter = this.map.getFilter(this.mapboxRemoteLayer.layerId);
        const modelFilters = objectIds.map((objectId) => ["!=", ["get", "id"], objectId]);
        const updatedFilter = updateLayerFilters(currentFilter, ...modelFilters);
        this.map.setFilter(this.mapboxRemoteLayer.layerId, updatedFilter);
    }
}
